import { useEffect, useState } from "react";
import { api } from "../lib/api";
import { formatApiError } from "../context/AuthContext";
import { toast } from "sonner";
import { Card } from "./ui/card";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { Textarea } from "./ui/textarea";
import { CheckCircle2, XCircle, StickyNote, Plus, Trash2, Loader2, Save } from "lucide-react";

function EditableList({ title, Icon, items, setItems, canEdit, testid }) {
  return (
    <div>
      <div className="text-xs uppercase tracking-widest text-muted-foreground font-semibold mb-2 flex items-center gap-1.5"><Icon className="h-3.5 w-3.5 text-primary" /> {title}</div>
      <div className="space-y-2">
        {items.map((v, i) => (
          <div key={i} className="flex items-start gap-2" data-testid={`${testid}-item-${i}`}>
            {canEdit ? (
              <>
                <Input value={v} onChange={(e) => setItems(items.map((x, idx) => (idx === i ? e.target.value : x)))} className="h-8 text-sm" data-testid={`${testid}-input-${i}`} />
                <button type="button" onClick={() => setItems(items.filter((_, idx) => idx !== i))} data-testid={`${testid}-remove-${i}`} className="mt-1.5"><Trash2 className="h-4 w-4 text-muted-foreground hover:text-destructive" /></button>
              </>
            ) : (
              <span className="text-sm text-muted-foreground">• {v}</span>
            )}
          </div>
        ))}
      </div>
      {canEdit && <Button size="sm" variant="ghost" className="mt-2" onClick={() => setItems([...items, ""])} data-testid={`${testid}-add`}><Plus className="h-4 w-4 mr-1.5" />Aggiungi</Button>}
    </div>
  );
}

export default function PreventivoCondizioni({ leadId, canEdit }) {
  const [inclusioni, setInclusioni] = useState([]);
  const [esclusioni, setEsclusioni] = useState([]);
  const [note, setNote] = useState("");
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    api.get(`/leads/${leadId}/preventivo-condizioni`).then((r) => {
      setInclusioni(r.data.inclusioni || []);
      setEsclusioni(r.data.esclusioni || []);
      setNote(r.data.note || "");
    }).finally(() => setLoading(false));
  }, [leadId]);

  const save = async () => {
    setSaving(true);
    try {
      await api.post(`/leads/${leadId}/preventivo-condizioni`, {
        inclusioni: inclusioni.map((s) => s.trim()).filter(Boolean),
        esclusioni: esclusioni.map((s) => s.trim()).filter(Boolean),
        note,
      });
      toast.success("Condizioni salvate");
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    } finally {
      setSaving(false);
    }
  };

  if (loading) return null;

  return (
    <Card className="rounded border-border p-5" data-testid="preventivo-condizioni">
      <div className="flex items-center justify-between mb-1">
        <h2 className="font-heading font-semibold">Inclusioni, esclusioni e note</h2>
        {canEdit && <Button size="sm" onClick={save} disabled={saving} className="font-semibold" data-testid="condizioni-save-btn">{saving ? <Loader2 className="h-4 w-4 mr-1.5 animate-spin" /> : <Save className="h-4 w-4 mr-1.5" />}Salva</Button>}
      </div>
      <p className="text-xs text-muted-foreground mb-5">Questi contenuti compaiono lato cliente nell'outlook. Modificabili solo dal referente di progetto.</p>
      <div className="grid lg:grid-cols-2 gap-6">
        <EditableList title="Servizi inclusi" Icon={CheckCircle2} items={inclusioni} setItems={setInclusioni} canEdit={canEdit} testid="incl" />
        <EditableList title="Esclusioni" Icon={XCircle} items={esclusioni} setItems={setEsclusioni} canEdit={canEdit} testid="escl" />
      </div>
      <div className="mt-6">
        <div className="text-xs uppercase tracking-widest text-muted-foreground font-semibold mb-2 flex items-center gap-1.5"><StickyNote className="h-3.5 w-3.5 text-primary" /> Note</div>
        <Textarea value={note} onChange={(e) => setNote(e.target.value)} disabled={!canEdit} rows={4} placeholder="Note aggiuntive visibili al cliente..." data-testid="condizioni-note" />
      </div>
    </Card>
  );
}
